/**
 * What a quick note gets filed under when nothing has been chosen by hand.
 *
 * A note typed mid-morning is about what is happening mid-morning, so the
 * clock is enough to name the lesson. Kept out of the dialog so the rules can
 * be tested without a DOM: everything here is derived, nothing is stored.
 */

import { closureForISO, schoolDayPositionISO } from './calendar.ts';
import { toLocalISO } from './dates.ts';
import { slotAtTime } from './slots.ts';

/** The part of a lesson this needs; anything wider would be untestable. */
export interface TimetabledLesson {
  id: string;
  weekStart: string;
  day: number;
  slot: number;
}

export interface QuickNoteContext<T extends TimetabledLesson> {
  /** ISO date to file the observation under. */
  date: string;
  /** Today's lessons in timetable order — what the picker offers. */
  dayLessons: T[];
  /** The one the clock points at, or null when there is nothing to point at. */
  current: T | null;
}

/**
 * The lessons of one date in timetable order, whichever date the dialog holds.
 *
 * Empty on a weekend, a holiday, a date outside the year, or a date field that
 * is blank or half-typed: the note still gets a date, but there is no
 * timetable to attach it to.
 */
export function lessonsOnDay<T extends TimetabledLesson>(lessons: readonly T[], iso: string): T[] {
  const position = schoolDayPositionISO(iso);
  if (!position || closureForISO(iso)) return [];

  return lessons
    .filter((l) => l.weekStart === position.weekStart && l.day === position.day)
    .sort((a, b) => a.slot - b.slot);
}

export function quickNoteContext<T extends TimetabledLesson>(
  lessons: readonly T[],
  now: Date,
): QuickNoteContext<T> {
  const date = toLocalISO(now);
  const dayLessons = lessonsOnDay(lessons, date);

  const at = slotAtTime(now);
  if (!at) return { date, dayLessons, current: null };

  const current =
    dayLessons.find((l) => l.slot === at.slot) ??
    // Free period, break, or after school: the note is about the last lesson
    // that actually happened, not about the empty slot the clock landed in.
    [...dayLessons].reverse().find((l) => l.slot < at.slot) ??
    null;

  return { date, dayLessons, current };
}
